import type { ReactElement, ReactNode } from 'react';
import { Tooltip as BaseTooltip } from '@base-ui/react/tooltip';
import { Info } from 'lucide-react';
import { cn } from '../../lib/cn.ts';
import { IconButton } from '../icon-button/icon-button.tsx';

export type TooltipSide = NonNullable<BaseTooltip.Positioner.Props['side']>;
export type TooltipAlign = NonNullable<BaseTooltip.Positioner.Props['align']>;

/*
 * Base UI waits 600ms before opening. That is right for a tooltip that repeats
 * a visible label; ours mostly carry the only copy there is — the sentence
 * behind an info bubble — so they open at a third of that.
 */
const DEFAULT_DELAY = 200;

export interface TooltipProps {
  /** The words shown on hover and focus. Never the only place a fact lives. */
  content: ReactNode;
  /**
   * The trigger. Rendered *as* this element rather than wrapped in one, so a
   * tooltip adds no box, no second focus ring and no extra tab stop.
   */
  children: ReactElement;
  side?: TooltipSide;
  align?: TooltipAlign;
  /** How long the pointer must rest before it opens. Focus opens it at once. */
  delay?: number;
  className?: string;
}

/**
 * A line of text about the thing under the pointer.
 *
 * Hover, focus and Escape all come from Base UI, which is the reason to wrap it
 * rather than draw one: a tooltip that only answers to the mouse is a tooltip
 * half the people on the page never see.
 */
export function Tooltip({
  content,
  children,
  side = 'top',
  align = 'center',
  delay = DEFAULT_DELAY,
  className,
}: TooltipProps) {
  return (
    <BaseTooltip.Root>
      <BaseTooltip.Trigger delay={delay} render={children} />
      <BaseTooltip.Portal>
        <BaseTooltip.Positioner side={side} align={align} sideOffset={6} className='z-50'>
          <BaseTooltip.Popup
            data-slot='tooltip'
            className={cn(
              'max-w-64 rounded-sm bg-primary px-2.5 py-1.5 text-xs font-semibold text-white shadow-menu animate-pop',
              className,
            )}
          >
            {content}
          </BaseTooltip.Popup>
        </BaseTooltip.Positioner>
      </BaseTooltip.Portal>
    </BaseTooltip.Root>
  );
}

export interface InfoTipProps extends Omit<TooltipProps, 'children' | 'className'> {
  /**
   * The button's accessible name. Defaults to the content when that is a
   * string — an icon with no name is a button a screen reader cannot announce.
   */
  label?: string;
  /** Classes for the button, not the popup: this is the part that sits in a row. */
  className?: string;
}

/**
 * The info bubble beside a label: what this field means, in one sentence.
 *
 * A button rather than a bare icon on purpose — it is reachable by keyboard,
 * and pressing it opens the same tooltip that hovering does.
 */
export function InfoTip({ content, label, className, ...tooltip }: InfoTipProps) {
  const name = label ?? (typeof content === 'string' ? content : 'More information');

  return (
    <Tooltip content={content} {...tooltip}>
      <IconButton
        data-slot='info-tip'
        aria-label={name}
        variant='quiet'
        size='sm'
        className={cn('text-ink-muted', className)}
      >
        <Info className='size-3.5' aria-hidden='true' />
      </IconButton>
    </Tooltip>
  );
}
